import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { ROUTES } from '@/routes';
import { useAuth } from '@/contexts/AuthContext';
import { PRIMARY_NAV_LINKS } from './nav-links';

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const { t } = useTranslation(['nav']);
  const { session, signOut } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('nav:mobile.label')}
      className="fixed inset-0 z-50 md:hidden"
    >
      <button
        type="button"
        aria-label={t('nav:mobile.close_menu')}
        onClick={onClose}
        className="absolute inset-0 bg-background/80 backdrop-blur-sm focus-visible:outline-2 focus-visible:outline-brand"
      />
      <div className="absolute inset-x-0 top-0 border-b border-border bg-background p-6 shadow-elevated">
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('nav:mobile.close_menu')}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label={t('nav:mobile.label')} className="mt-4 flex flex-col gap-1">
          {PRIMARY_NAV_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onClose}
              className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t(item.i18nKey, { ns: 'nav' })}
            </Link>
          ))}
          <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
            {session ? (
              <>
                <Link
                  to={ROUTES.account}
                  onClick={onClose}
                  className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('nav:auth.account')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    void signOut();
                  }}
                  className="rounded-md border border-border px-3 py-3 text-base font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('nav:auth.sign_out')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to={ROUTES.authSignIn}
                  onClick={onClose}
                  className="rounded-md px-3 py-3 text-base font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('nav:auth.sign_in')}
                </Link>
                <Link
                  to={ROUTES.authSignUp}
                  onClick={onClose}
                  className="rounded-md bg-brand px-3 py-3 text-center text-base font-semibold text-brand-foreground shadow-soft hover:bg-brand-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('nav:auth.sign_up')}
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}
